import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { StandardEvents } from '@shopify/events';

class TrimPackageContent extends Component {
  /** @type {AbortController | undefined} */
  #listeners;

  #selectionVersion = 0;

  /** @type {HTMLElement | undefined} */
  #content;

  connectedCallback() {
    super.connectedCallback();
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    const scope = this.closest('.shopify-section, dialog');
    scope?.addEventListener(StandardEvents.productSelect, this.#handleSelection, {
      signal: this.#listeners.signal,
    });
    this.#syncPicker();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#syncPicker();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.restoreContent();
    this.#listeners?.abort();
    this.#selectionVersion++;
  }

  #syncPicker() {
    const content = this.querySelector('[data-trim-content]');
    if (content instanceof HTMLElement) this.#content = content;
    const scope = this.closest('.shopify-section, dialog');
    const pickers = scope?.querySelectorAll('variant-picker') ?? [];
    for (const picker of pickers) {
      if (!(picker instanceof HTMLElement) || picker.dataset.productId !== this.dataset.productId) continue;
      /** @type {NodeListOf<HTMLInputElement | HTMLOptionElement>} */
      const selected = picker.querySelectorAll('input:checked, option:checked, input[data-single-option]');
      const trim = Array.from(selected).find((value) => value.dataset.optionName === this.dataset.optionName);
      if (trim) {
        const group = trim.closest('[data-option-position]');
        if (group && this.#content && group.nextElementSibling !== this.#content) {
          group.insertAdjacentElement('afterend', this.#content);
        }
        this.#showTrim(trim.value);
      }
      break;
    }
  }

  // Keep the component at its editor location; only its presentation moves into the picker.
  restoreContent() {
    if (this.#content && this.#content.parentElement !== this) this.append(this.#content);
  }

  placeContent() {
    this.#syncPicker();
  }

  /**
   * @param {string | undefined} name
   */
  #showTrim(name) {
    for (const panel of this.#content?.querySelectorAll('[data-trim-name]') ?? []) {
      if (panel instanceof HTMLElement) panel.hidden = panel.dataset.trimName !== name;
    }
  }

  /**
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #handleSelection = (event) => {
    if (!(event.target instanceof HTMLElement) || event.target.closest('product-card')) return;
    if (event.target.dataset.productId !== this.dataset.productId) return;

    const version = ++this.#selectionVersion;
    const selected = event.selectedOptions.find((option) => option.name === this.dataset.optionName);
    this.#showTrim(selected?.value);

    event.promise
      .then(({ detail }) => {
        if (!this.isConnected || version !== this.#selectionVersion || !detail?.html) return;
        const source = Array.from(detail.html.querySelectorAll('trim-package-content')).find(
          (block) => block instanceof HTMLElement && block.dataset.blockId === this.dataset.blockId
        );
        if (source instanceof HTMLElement) {
          this.dataset.productId = source.dataset.productId;
          this.dataset.optionName = source.dataset.optionName;
          this.restoreContent();
          morph(this, source, { childrenOnly: true });
          this.#content = undefined;
          this.#syncPicker();
        } else {
          this.#showTrim(undefined);
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[trim-package-content] Selection update failed:', error);
      });
  };
}

if (!customElements.get('trim-package-content')) {
  customElements.define('trim-package-content', TrimPackageContent);
}
